import { useEffect, useState } from 'react';

/**
 * Minimum viewport width (CSS px) the Intelligence Terminal is built for.
 *
 * Arcanum is a desktop-first, data-dense underwriting terminal (Bloomberg-style)
 * — phones and portrait tablets cannot render its layouts. Below this width the
 * app surfaces a full-screen "built for desktop" notice instead of booting the
 * provider + router tree (see `<DesktopOnlyGate>` mounted at the top of `App`).
 *
 * 1024 = Tailwind's `lg` breakpoint. It blocks every phone and every
 * portrait tablet; a tablet in LANDSCAPE (>=1024) gets the real `lg` layout,
 * which renders acceptably. Bump to 1280 to also exclude landscape tablets —
 * the trade-off is that scaled-resolution / narrow-window laptops would then
 * hit the gate too (the main false-positive risk), which is why 1024 is the
 * default.
 *
 * Width-based (NOT user-agent) is deliberate: UA sniffing is brittle (iPadOS
 * reports as desktop Safari, "request desktop site" lies, new devices break the
 * list) and a narrow desktop window genuinely can't render the terminal either.
 * Width is the real constraint, and the check is reactive — widen the window
 * and the gate dissolves.
 */
export const MIN_VIEWPORT_WIDTH = 1024;

function readBelow(minWidth: number): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < minWidth;
}

/**
 * Reactively reports whether the viewport is narrower than `minWidth`.
 * SSR-safe (returns `false` when `window` is absent) and re-evaluates on every
 * `resize` (covers device rotation + window resize without a reload).
 */
export function useViewportBelow(
  minWidth: number = MIN_VIEWPORT_WIDTH
): boolean {
  const [below, setBelow] = useState<boolean>(() => readBelow(minWidth));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setBelow(readBelow(minWidth));
    // Sync once on mount in case the SSR/initial-state read drifted from the
    // live viewport (hydration, fast rotate before effect attaches).
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth]);

  return below;
}
